import { ulid } from 'ulid';
import { encodeStoredTemplate, parseStoredTemplate } from '../../utils/displayTemplate';

// Pure model for the display-template builder.
//
// A template is modeled as alternating text slots and element tokens:
//
//   texts[0] token[0] texts[1] token[1] ... token[n-1] texts[n]
//
// INVARIANT: texts.length === tokens.length + 1, always. Text slots may be
// empty strings; they are what lets the user type separators before, between,
// and after pills. Tokens hold the element reference plus its chosen
// formatter. Every operation below is immutable and preserves the invariant,
// so the view layer can stay dumb and the whole thing round-trips through the
// persisted `{{element | formatter(args)}}` string encoding.

export const createToken = ({ elementName, formatterName = null, formatArgs = [] }) => ({
	id: ulid(),
	elementName: elementName || '',
	formatterName: formatterName || null,
	formatArgs: Array.isArray(formatArgs) ? formatArgs : [],
});

export const emptyModel = () => ({ texts: [''], tokens: [] });

// Accepts either stored encoding (v2 JSON envelope or legacy {{ }} DSL).
export const fromTemplate = (template = '') => {
	const segments = parseStoredTemplate(template || '');
	const texts = [''];
	const tokens = [];
	segments.forEach(segment => {
		if (segment.type === 'text') {
			texts[texts.length - 1] += segment.value || '';
			return;
		}
		if (segment.type === 'element') {
			const formatter = segment.formatters?.[0];
			tokens.push(createToken({
				elementName: segment.name,
				formatterName: formatter?.name || null,
				formatArgs: (formatter?.args || []).map(arg => arg ?? ''),
			}));
			texts.push('');
		}
	});
	return { texts, tokens };
};

// Always emits the v2 JSON envelope — legacy DSL templates upgrade the next
// time the card is saved.
export const toTemplate = (model) => {
	const { texts, tokens } = model;
	const segments = [];
	texts.forEach((text, idx) => {
		if (text) segments.push({ type: 'text', value: text });
		const token = tokens[idx];
		if (token) {
			segments.push({
				type: 'element',
				name: token.elementName,
				formatters: token.formatterName
					? [{ name: token.formatterName, args: token.formatArgs || [] }]
					: [],
			});
		}
	});
	return encodeStoredTemplate(segments);
};

// Insert a token at slot `slotIndex` (0..tokens.length). When `cursor` is
// given, the text in slot `slotIndex` is split at that offset so the token
// lands exactly where the user's caret was.
export const insertToken = (model, slotIndex, token, cursor = null) => {
	const texts = [...model.texts];
	const tokens = [...model.tokens];
	const slot = Math.max(0, Math.min(slotIndex, tokens.length));
	const current = texts[slot] || '';
	const splitAt = cursor === null || cursor === undefined
		? current.length
		: Math.max(0, Math.min(cursor, current.length));
	texts[slot] = current.slice(0, splitAt);
	texts.splice(slot + 1, 0, current.slice(splitAt));
	tokens.splice(slot, 0, token);
	return { texts, tokens };
};

// Remove token `index`, merging the surrounding text slots.
export const removeToken = (model, index) => {
	if (index < 0 || index >= model.tokens.length) return model;
	const texts = [...model.texts];
	const tokens = [...model.tokens];
	tokens.splice(index, 1);
	texts[index] = (texts[index] || '') + (texts[index + 1] || '');
	texts.splice(index + 1, 1);
	return { texts, tokens };
};

// Move a token to another position; text slots keep their places, so
// separators the user typed stay anchored where they were.
export const moveToken = (model, fromIndex, toIndex) => {
	if (fromIndex === toIndex) return model;
	if (fromIndex < 0 || fromIndex >= model.tokens.length) return model;
	const tokens = [...model.tokens];
	const [moved] = tokens.splice(fromIndex, 1);
	tokens.splice(Math.max(0, Math.min(toIndex, tokens.length)), 0, moved);
	return { texts: [...model.texts], tokens };
};

export const updateText = (model, slotIndex, value) => {
	if (slotIndex < 0 || slotIndex >= model.texts.length) return model;
	const texts = [...model.texts];
	texts[slotIndex] = value ?? '';
	return { texts, tokens: [...model.tokens] };
};

export const updateTokenFormat = (model, index, formatterName, formatArgs = []) => {
	if (index < 0 || index >= model.tokens.length) return model;
	const tokens = model.tokens.map((token, i) => (
		i === index
			? { ...token, formatterName: formatterName || null, formatArgs: Array.isArray(formatArgs) ? formatArgs : [] }
			: token
	));
	return { texts: [...model.texts], tokens };
};
